// frontend/src/components/MonitoringPage.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    Button,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    // Добавлено для диалога подтверждения
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    // --- НОВОЕ: Импорты для NVR ---
    IconButton,
    Tooltip,
    Snackbar, // Для уведомления о копировании
    Alert as MuiAlert, // Альтернативный Alert из MUI для Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonitorIcon from '@mui/icons-material/Monitor'; // Новая иконка для VNC
// Импортируем иконку для новой кнопки
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
// --- НОВОЕ: Импорт иконки для камеры ---
import VideocamIcon from '@mui/icons-material/Videocam';
// --- НОВОЕ: Импорт нового компонента ---
import NvrVideoModal from './NvrVideoModal'; // Убедитесь, что путь правильный

const API_BASE_URL = ``;

// Определяем заголовки таблицы для мониторинга
// Добавляем поле checkEndpoint для метрик, которые можно проверять отдельно
// Определяем заголовки таблицы для мониторинга
const headCells = [
    { id: 'actions', label: 'Действия', sortable: false, filterable: false },
    { id: 'ip', label: 'IP', sortable: true, filterable: true },
    { id: 'city', label: 'Город', sortable: true, filterable: true },
    { id: 'trk', label: 'ТРК', sortable: true, filterable: true },
    { id: 'pos_status', label: 'POS', sortable: true, filterable: true, checkEndpoint: 'pos' },
    { id: 'disk_c_free_space', label: 'Disk C:', sortable: true, filterable: true, checkEndpoint: 'disks' },
    { id: 'disk_d_free_space', label: 'Disk D:', sortable: true, filterable: true, checkEndpoint: 'disks' },
    { id: 'fmu_api_status', label: 'fmu-api', sortable: true, filterable: true, checkEndpoint: 'services' },
    { id: 'lmchz_status', label: 'ЛМЧЗ', sortable: true, filterable: true, checkEndpoint: 'lmchz' },
    { id: 'nvr_status', label: 'NVR', sortable: true, filterable: true, checkEndpoint: 'nvr' },
    { id: 'counter_status', label: 'Counter', sortable: true, filterable: true, checkEndpoint: 'counter' },
    { id: 'firmware_version', label: 'Прошивка ФР', sortable: true, filterable: true, checkEndpoint: 'services' },
    { id: 'po_date', label: 'Версия ПО ФР', sortable: true, filterable: true, checkEndpoint: 'services' },
	{ id: 'fr_subscription_status', label: 'Подписка ФР', sortable: true, filterable: true, checkEndpoint: 'services' },
    { id: 'uptime_pos', label: 'Uptime POS', sortable: true, filterable: true, checkEndpoint: 'uptime' },
    { id: 'mikrotik_model', label: 'Mikrotik Model', sortable: true, filterable: true, checkEndpoint: 'mikrotik' },
    { id: 'mikrotik_firmware', label: 'Mikrotik Firmware', sortable: true, filterable: true, checkEndpoint: 'mikrotik' },
    { id: 'mikrotik_uptime', label: 'Mikrotik Uptime', sortable: true, filterable: true, checkEndpoint: 'mikrotik' },
    { id: 'last_checked_at', label: 'Последняя проверка', sortable: true, filterable: true },
];

// Вспомогательная функция для форматирования даты и времени
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Нет данных';
    try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return 'Неверная дата';
    }
};

// Вспомогательная функция для форматирования статуса подписки ФР
const formatFrSubscriptionStatus = (status) => {
    if (!status || status === 'Н/Д') return status;
    // Ищем шаблон "Подписка действительна до X квартала YYYY года"
    const regex = /Подписка\s+действительна\s+до\s+(\d+)\s+квартала\s+(\d{4})\s+года/i;
    const match = status.match(regex);
    if (match) {
        const quarter = match[1]; // 1, 2, 3 или 4
        const year = match[2];   // 2021, 2024 и т.д.
        return `${quarter}/${year}`;
    }
    // Если шаблон не найден, возвращаем исходное значение
    return status;
};

// Вспомогательная функция для получения значения поля из объекта устройства
const getFieldValue = (device, fieldId) => {
    if (fieldId === 'ip' || fieldId === 'city' || fieldId === 'trk') {
        return device[fieldId] || '';
    }
    if (fieldId === 'actions') {
        return ''; // Значение для колонки действий
    }
    // Для полей мониторинга
    if (device.monitoring_status) {
        return device.monitoring_status[fieldId] || '';
    }
    return '';
};

// Функция для определения стиля ячейки
const getCellStyle = (value, fieldId) => {
    // Список полей, которые не должны окрашиваться
    // --- ОБНОВЛЕНО: Добавлены поля Mikrotik и uptime_pos ---
    const noColorFields = ['ip', 'city', 'trk', 'last_checked_at', 'firmware_version', 'fr_subscription_status', 'po_date', 'actions', 'uptime_pos', 'mikrotik_model', 'mikrotik_firmware', 'mikrotik_uptime'];
    // --- КОНЕЦ ОБНОВЛЕНИЯ ---
    // Если поле в списке исключений, не применяем цвет
    if (noColorFields.includes(fieldId)) {
        return {};
    }
    if (!value || value === 'Н/Д') return {};
    const lowerValue = String(value).toLowerCase();
    // Проверяем, является ли значение числом (например, "78.1%")
    const percentageMatch = value.match(/(\d+(\.\d+)?)/);
    if (percentageMatch) {
        const percentage = parseFloat(percentageMatch[1]);
        // Определяем цвет на основе процента для дисков
        if (percentage >= 90) {
            return { bgcolor: '#c62828', color: '#ffebee' }; // Красный фон, светло-красный текст
        } else if (percentage >= 85) {
            return { bgcolor: '#ffeb3b', color: '#f57f17' }; // Желтый фон, темно-желтый текст
        } else {
            return { bgcolor: '#4caf50', color: '#fff' }; // Зеленый фон, белый текст
        }
    }
    // Для других типов значений используем существующую логику
    if (
        lowerValue.includes('не в сети') ||
        lowerValue.includes('не работает') ||
        lowerValue.includes('ошибка') ||
        lowerValue.includes('недоступно') ||
        lowerValue.includes('таймаут') ||
        lowerValue.includes('нет подключения') ||
        lowerValue === 'нет ip' ||
        lowerValue.includes('http') ||
        lowerValue.includes('ошибка подключения') ||
        lowerValue.includes('Н/Д') ||
        lowerValue.includes('sync_error')
    ) {
        return { bgcolor: '#c62828', color: '#ffebee' }; // Красный фон, светло-красный текст
    }
    if (
        lowerValue.includes('в сети') ||
        lowerValue.includes('работает') ||
        lowerValue.includes('готово') ||
        lowerValue.includes('активна') ||
        lowerValue.includes('активно') ||
        lowerValue.includes('ready') ||
        lowerValue.includes('пропущено') ||
        lowerValue.includes('готов') ||
        lowerValue === 'готов'
    ) {
        return { bgcolor: '#4caf50', color: '#fff' }; // Зеленый фон, белый текст
    }
    if (
        lowerValue.includes('предупреждение') ||
        lowerValue.includes('внимание') ||
        lowerValue.includes('требуется обновление')
    ) {
        return { bgcolor: '#ffeb3b', color: '#f57f17' }; // Желтый фон, темно-желтый текст
    }
    return {};
};

function MonitoringPage() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [order, setOrder] = useState('asc'); // направление сортировки
    const [orderBy, setOrderBy] = useState('ip'); // поле для сортировки
    const [columnFilters, setColumnFilters] = useState({}); // фильтры по колонкам
    // Состояния для новой кнопки и диалога
    const [isTriggering, setIsTriggering] = useState(false); // Состояние загрузки для кнопки "Проверить всё"
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false); // Состояние диалога подтверждения
    const [triggeringMetric, setTriggeringMetric] = useState(null); // Какая метрика сейчас проверяется
    const [triggeringDeviceId, setTriggeringDeviceId] = useState(null); // Какое устройство сейчас проверяется
    // --- НОВОЕ: Состояние для инициализации ЛМЧЗ ---
    const [initializingLmchzId, setInitializingLmchzId] = useState(null); // ID устройства, которое инициализируется

    // --- НОВОЕ: Состояния для NVR Modal ---
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [currentDeviceForVideo, setCurrentDeviceForVideo] = useState(null); // Храним объект устройства

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        setMessage('');
        try {
            const response = await axios.get(`/devices_with_monitoring/`);
            setData(response.data);
            setMessage('Данные мониторинга успешно загружены.');
        } catch (err) {
            console.error("Ошибка при загрузке данных мониторинга:", err);
            setError(err);
            setMessage(`Ошибка при загрузке данных: ${err.message || 'Неизвестная ошибка'}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        //const intervalId = setInterval(fetchData, 30000);
        //return () => clearInterval(intervalId);
    }, [fetchData]);

    // Обработка сортировки
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Обработка изменения фильтра колонки
    const handleFilterChange = (fieldId, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    // Получение уникальных значений для фильтрации
    const getUniqueValues = (fieldId) => {
        const values = data.map(device => getFieldValue(device, fieldId))
            .filter(value => value !== null && value !== undefined && value !== '');
        return [...new Set(values)].sort();
    };

    // Сортировка данных
    const sortedData = useMemo(() => {
        if (!orderBy) return data;
        return [...data].sort((a, b) => {
            let aValue = getFieldValue(a, orderBy);
            let bValue = getFieldValue(b, orderBy);
            // Преобразование значений для корректной сортировки
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
            }
            if (typeof bValue === 'string') {
                bValue = bValue.toLowerCase();
            }
            // Сортировка числовых значений
            if (!isNaN(aValue) && !isNaN(bValue)) {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            }
            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [data, order, orderBy]);

    // Фильтрация данных
    const filteredData = useMemo(() => {
        let result = sortedData;
        // Общий поиск
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            result = result.filter(device =>
                Object.values(device).some(value =>
                    String(value).toLowerCase().includes(lowerCaseSearchTerm)
                ) || (device.monitoring_status && Object.values(device.monitoring_status).some(value =>
                    String(value).toLowerCase().includes(lowerCaseSearchTerm)
                ))
            );
        }
        // Фильтрация по колонкам
        Object.keys(columnFilters).forEach(fieldId => {
            const filterValue = columnFilters[fieldId];
            if (filterValue && filterValue.length > 0) {
                result = result.filter(device => {
                    const fieldValue = getFieldValue(device, fieldId);
                    return filterValue.includes(String(fieldValue));
                });
            }
        });
        return result;
    }, [sortedData, searchTerm, columnFilters]);

    // Получение значения для отображения с учетом форматирования
    const getDisplayValue = (device, fieldId) => {
        if (fieldId === 'ip' || fieldId === 'city' || fieldId === 'trk' || fieldId === 'actions') {
            return device[fieldId] || 'Н/Д';
        }
        if (fieldId === 'po_date') {
            const dateValue = device.monitoring_status?.[fieldId];
            return dateValue ? new Date(dateValue).toLocaleDateString('ru-RU') : 'Н/Д';
        }
        if (fieldId === 'last_checked_at') {
            return formatDateTime(device.monitoring_status?.[fieldId]);
        }
        // Для остальных полей мониторинга
        return device.monitoring_status?.[fieldId] || 'Н/Д';
    };

    // Новая функция для вызова эндпоинта запуска ВСЕХ проверок
    const handleTriggerAllChecks = useCallback(async () => {
        setIsTriggering(true);
        setTriggeringMetric(null); // Сброс конкретной метрики
        setTriggeringDeviceId(null); // Сброс конкретного устройства
        setError(null);
        setMessage('');
        try {
            // Вызываем эндпоинт из main.py
            const response = await axios.post(`/run-device-checks/`);
            setMessage(response.data.message || 'Все проверки запущены.');
            // Опционально: обновить данные сразу или через короткий интервал
            setTimeout(() => {
                fetchData();
            }, 5000);
        } catch (err) {
            console.error("Ошибка при запуске ВСЕХ проверок:", err);
            if (err.response) {
                setError(new Error(`Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`));
            } else if (err.request) {
                setError(new Error('Сервер не отвечает.'));
            } else {
                setError(new Error(`Ошибка запроса: ${err.message}`));
            }
            setMessage(`Ошибка при запуске проверок: ${err.message || 'Неизвестная ошибка'}`);
        } finally {
            setIsTriggering(false);
            setOpenConfirmDialog(false); // Закрыть диалог после завершения
        }
    }, [fetchData]);

    // Новая функция для вызова эндпоинта запуска проверки конкретной метрики
    const handleTriggerMetricCheck = useCallback(async (endpointName, metricLabel) => {
        setIsTriggering(true);
        setTriggeringMetric(endpointName); // Установить, какая метрика проверяется
        setTriggeringDeviceId(null); // Сброс конкретного устройства
        setError(null);
        setMessage('');
        try {
            // Вызываем соответствующий эндпоинт
            const response = await axios.post(`/check_${endpointName}/`);
            setMessage(response.data.message || `Проверка "${metricLabel}" запущена.`);
            // Опционально: обновить данные сразу или через короткий интервал
            setTimeout(() => {
                fetchData();
            }, 5000);
        } catch (err) {
            console.error(`Ошибка при запуске проверки ${endpointName}:`, err);
            if (err.response) {
                setError(new Error(`Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`));
            } else if (err.request) {
                setError(new Error('Сервер не отвечает.'));
            } else {
                setError(new Error(`Ошибка запроса: ${err.message}`));
            }
            setMessage(`Ошибка при запуске проверки "${metricLabel}": ${err.message || 'Неизвестная ошибка'}`);
        } finally {
            setIsTriggering(false);
            setTriggeringMetric(null); // Сброс конкретной метрики
        }
    }, [fetchData]);

    // Новая функция для вызова эндпоинта проверки конкретного устройства
    const handleTriggerDeviceCheck = useCallback(async (deviceId) => {
        setIsTriggering(true);
        setTriggeringDeviceId(deviceId); // Установить, какое устройство проверяется
        setTriggeringMetric(null); // Сброс конкретной метрики
        setError(null);
        setMessage('');
        try {
            // Вызываем соответствующий эндпоинт
            const response = await axios.post(`/check_device/${deviceId}`);
            setMessage(response.data.message || `Проверка устройства запущена.`);
            // Опционально: обновить данные сразу или через короткий интервал
            setTimeout(() => {
                fetchData();
            }, 5000);
        } catch (err) {
            console.error(`Ошибка при запуске проверки устройства с ID ${deviceId}:`, err);
            if (err.response) {
                setError(new Error(`Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`));
            } else if (err.request) {
                setError(new Error('Сервер не отвечает.'));
            } else {
                setError(new Error(`Ошибка запроса: ${err.message}`));
            }
            setMessage(`Ошибка при запуске проверки устройства: ${err.message || 'Неизвестная ошибка'}`);
        } finally {
            setIsTriggering(false);
            setTriggeringDeviceId(null); // Сброс конкретного устройства
        }
    }, [fetchData]);

    // --- НОВОЕ: Функция для вызова эндпоинта инициализации ЛМЧЗ ---
    const handleInitLmchz = useCallback(async (deviceId) => {
        setInitializingLmchzId(deviceId); // Установить состояние загрузки для конкретного устройства
        setError(null);
        setMessage('');
        try {
            const response = await axios.post(`/init_lmchz/${deviceId}`);
            setMessage(response.data.message || 'Инициализация ЛМЧЗ запущена.');
            // Опционально: обновить данные таблицы через некоторое время
            // setTimeout(() => {
            //     fetchData();
            // }, 3000);
        } catch (err) {
            console.error(`Ошибка при инициализации ЛМЧЗ для устройства с ID ${deviceId}:`, err);
            if (err.response) {
                setError(new Error(`Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`));
            } else if (err.request) {
                setError(new Error('Сервер не отвечает.'));
            } else {
                setError(new Error(`Ошибка запроса: ${err.message}`));
            }
            setMessage(`Ошибка при инициализации ЛМЧЗ: ${err.message || 'Неизвестная ошибка'}`);
        } finally {
            setInitializingLmchzId(null); // Сброс состояния загрузки
        }
    }, []);

    // --- НОВОЕ: Функция для обработки клика по кнопке камеры NVR ---
    const handleNvrCameraClick = useCallback((device) => {
        setCurrentDeviceForVideo(device);
        setVideoModalOpen(true);
    }, []);

    // --- НОВОЕ: Функция для закрытия модального окна ---
    const handleCloseVideoModal = useCallback(() => {
        setVideoModalOpen(false);
        setCurrentDeviceForVideo(null);
    }, []);

    // Обработчики диалога подтверждения
    const handleClickOpenConfirmDialog = () => {
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
                Мониторинг Устройств
            </Typography>
            {loading && <CircularProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
            {message && !error && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

            {/* --- НОВОЕ: Модальное окно для видео (вынесено в отдельный компонент) --- */}
            <NvrVideoModal
                open={videoModalOpen}
                onClose={handleCloseVideoModal}
                device={currentDeviceForVideo}
            />
            {/* --- КОНЕЦ НОВОГО --- */}

            {/* Контейнер для кнопок */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    label="Поиск по мониторингу"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    margin="normal"
                    sx={{ flexGrow: 1, minWidth: '250px' }}
                />
                {/* Кнопка "Обновить данные" */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchData}
                    startIcon={<RefreshIcon />}
                    sx={{ height: '56px', mt: '16px' }}
                    disabled={isTriggering} // Отключить во время запуска проверок
                >
                    Обновить данные
                </Button>
                {/* Новая кнопка "Проверить всё" */}
                <Button
                    variant="contained"
                    color="secondary" // Используем другой цвет для выделения
                    onClick={handleClickOpenConfirmDialog} // Открываем диалог подтверждения
                    startIcon={<PlayArrowIcon />}
                    sx={{ height: '56px', mt: '16px' }}
                    disabled={isTriggering || loading} // Отключить во время загрузки или запуска
                >
                    {isTriggering && !triggeringMetric && !triggeringDeviceId ? <CircularProgress size={24} /> : 'Проверить всё'} {/* Показать спиннер во время выполнения */}
                </Button>
            </Box>

            {/* Диалог подтверждения */}
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Подтвердите действие"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Вы уверены, что хотите запустить проверку всех устройств? Это может занять некоторое время.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                     {/* Кнопка "Отмена" */}
                    <Button onClick={handleCloseConfirmDialog} color="primary" disabled={isTriggering}>
                        Отмена
                    </Button>
                    {/* Кнопка "Подтвердить" */}
                    <Button
                        onClick={handleTriggerAllChecks}
                        color="secondary"
                        autoFocus
                        disabled={isTriggering} // Отключить во время выполнения
                        startIcon={isTriggering && !triggeringMetric && !triggeringDeviceId ? <CircularProgress size={20} /> : null} // Иконка загрузки
                    >
                        {isTriggering && !triggeringMetric && !triggeringDeviceId ? 'Запуск...' : 'Подтвердить'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Таблица */}
            <TableContainer component={Paper}>
                <Table stickyHeader aria-label="monitoring data table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    sx={{
                                        color: 'text.primary',
                                        backgroundColor: 'background.paper',
                                        minWidth: headCell.id === 'client_name' ? 180 : 'auto',
                                        position: 'relative', // Для позиционирования кнопки
                                    }}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    {headCell.id === 'actions' ? (
                                        // Заголовок для колонки действий
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            {headCell.label}
                                        </Typography>
                                    ) : (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    {headCell.sortable ? (
                                                        <TableSortLabel
                                                            active={orderBy === headCell.id}
                                                            direction={orderBy === headCell.id ? order : 'asc'}
                                                            onClick={() => handleRequestSort(headCell.id)}
                                                        >
                                                            {headCell.label}
                                                        </TableSortLabel>
                                                    ) : (
                                                        headCell.label
                                                    )}
                                                    {/* Фильтр для колонки */}
                                                    {headCell.filterable && (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    multiple
                                                                    displayEmpty
                                                                    value={columnFilters[headCell.id] || []}
                                                                    onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                                                                    renderValue={(selected) => {
                                                                        if (selected.length === 0) {
                                                                            return <em>Все</em>;
                                                                        }
                                                                        return selected.join(', ');
                                                                    }}
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                                        '.MuiSvgIcon-root': { color: 'secondary.main' },
                                                                    }}
                                                                >
                                                                    <MenuItem disabled value="">
                                                                        <em>Выберите значения</em>
                                                                    </MenuItem>
                                                                    {getUniqueValues(headCell.id).map((value) => (
                                                                        <MenuItem key={value} value={value}>
                                                                            <Checkbox checked={(columnFilters[headCell.id] || []).indexOf(value) > -1} />
                                                                            <ListItemText primary={value} />
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            {/* Кнопка для запуска проверки конкретной метрики */}
                                                            {headCell.checkEndpoint && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary" // Используем цвет primary (синий)
                                                                    size="small"
                                                                    onClick={() => handleTriggerMetricCheck(headCell.checkEndpoint, headCell.label)}
                                                                    disabled={isTriggering}
                                                                    startIcon={isTriggering && triggeringMetric === headCell.checkEndpoint ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                                                    sx={{
                                                                        mt: 1, // Вертикальный отступ сверху
                                                                        padding: '6px', // Паддинг внутри кнопки
                                                                        width: '100%', // Ширина кнопки равна ширине контейнера
                                                                        minWidth: '40px', // Минимальная ширина кнопки
                                                                        justifyContent: 'center' // Центрируем иконку
                                                                    }}
                                                                >
                                                                    {/* Убираем текст, оставляем только иконку */}
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.length === 0 && !loading && !error ? (
                            <TableRow>
                                <TableCell colSpan={headCells.length} align="center">
                                    Нет данных мониторинга для отображения.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData.map((device) => (
                                <TableRow key={device.id}>
                                     {/* Кнопка "Проверить" для текущей строки */}
                                     <TableCell sx={{ width: '60px', textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            color="primary" // Используем цвет primary (синий)
                                            size="small"
                                            onClick={() => handleTriggerDeviceCheck(device.id)}
                                            disabled={isTriggering}
                                            startIcon={isTriggering && triggeringDeviceId === device.id ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                            sx={{
                                                minWidth: '40px', // Компактная кнопка с иконкой
                                                padding: '6px', // Паддинг внутри кнопки
                                                mt: 1, // Вертикальный отступ сверху
                                            }}
                                        >
                                            {/* Убираем текст, оставляем только иконку */}
                                        </Button>
                                    </TableCell>
                                    {/* Остальные ячейки таблицы */}
                                    <TableCell sx={getCellStyle(device.ip, 'ip')}>{device.ip || 'Н/Д'}</TableCell>
                                    <TableCell sx={getCellStyle(device.city, 'city')}>{device.city || 'Н/Д'}</TableCell>
                                    <TableCell sx={getCellStyle(device.trk, 'trk')}>{device.trk || 'Н/Д'}</TableCell>
									<TableCell sx={getCellStyle(device.monitoring_status?.pos_status, 'pos_status')}>
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
											<span>{device.monitoring_status?.pos_status || 'Н/Д'}</span>
											{device.ip && (
												<Tooltip title={`Открыть VNC: ${device.ip}`}>
													<IconButton
														size="small"
														onClick={() => {
															// Формируем базовый путь прокси
															const proxyPath = `vncproxy/${device.ip}`;
															// Указываем noVNC, что вебсокет нужно искать по адресу /vncproxy/IP/websockify
															const vncUrl = `${window.location.origin}/${proxyPath}/vnc.html?autoconnect=true&file_transfer=true&resize=scale&path=/${proxyPath}/websockify`;
															
															window.open(vncUrl, '_blank');
														}}
														sx={{ 
															color: 'inherit', 
															p: 0.5,
															backgroundColor: 'rgba(255, 255, 255, 0.2)',
															'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' }
														}}
													>
														<MonitorIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
										</Box>
									</TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.disk_c_free_space, 'disk_c_free_space')}>
                                        {device.monitoring_status?.disk_c_free_space || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.disk_d_free_space, 'disk_d_free_space')}>
                                        {device.monitoring_status?.disk_d_free_space || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.fmu_api_status, 'fmu_api_status')}>
                                        {device.monitoring_status?.fmu_api_status || 'Н/Д'}
                                    </TableCell>
									<TableCell sx={getCellStyle(device.monitoring_status?.lmchz_status, 'lmchz_status')}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span>{device.monitoring_status?.lmchz_status || 'Н/Д'}</span>
											<Button
												variant="contained"
												// color="warning" // Желтый цвет
												sx={{
													backgroundColor: '#ff9800', // Ярко-желтый (Orange 500)
													color: '#fff', // Белый текст
													'&:hover': {
														backgroundColor: '#f57c00', // Более темный оранжевый при наведении
													},
													minWidth: 'auto', // Минимальная ширина
													padding: '4px 8px', // Компактный паддинг
													fontSize: '0.75rem', // Меньший шрифт
												}}
												size="small"
												onClick={() => handleInitLmchz(device.id)}
												disabled={initializingLmchzId === device.id || isTriggering}
												startIcon={initializingLmchzId === device.id ? <CircularProgress size={16} color="inherit" /> : null}
											>
												{initializingLmchzId === device.id ? '...' : 'init'}
											</Button>
										</Box>
									</TableCell>
									<TableCell sx={getCellStyle(device.monitoring_status?.nvr_status, 'nvr_status')}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span>{device.monitoring_status?.nvr_status || 'Н/Д'}</span>
											{device.monitoring_status?.nvr_status && device.ip && (
												<Tooltip title="Открыть видеопоток камеры">
													<IconButton
														aria-label="открыть камеру"
														onClick={() => handleNvrCameraClick(device)}
														size="small"
														sx={{
															color: 'primary.main',
															'&:hover': {
																backgroundColor: 'rgba(25, 118, 210, 0.08)', // primary.light с прозрачностью
															},
														}}
													>
														<VideocamIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
										</Box>
									</TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.counter_status, 'counter_status')}>
                                        {device.monitoring_status?.counter_status || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.firmware_version, 'firmware_version')}>
                                        {device.monitoring_status?.firmware_version || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.po_date, 'po_date')}>
                                        {getDisplayValue(device, 'po_date')}
                                    </TableCell>
									<TableCell sx={getCellStyle(device.monitoring_status?.fr_subscription_status, 'fr_subscription_status')}>
										{formatFrSubscriptionStatus(device.monitoring_status?.fr_subscription_status) || 'Н/Д'}
									</TableCell>
									{/* --- НОВОЕ: Ячейки для uptime_pos и метрик Mikrotik --- */}
                                    <TableCell sx={getCellStyle(device.monitoring_status?.uptime_pos, 'uptime_pos')}>
                                        {device.monitoring_status?.uptime_pos || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.mikrotik_model, 'mikrotik_model')}>
                                        {device.monitoring_status?.mikrotik_model || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.mikrotik_firmware, 'mikrotik_firmware')}>
                                        {device.monitoring_status?.mikrotik_firmware || 'Н/Д'}
                                    </TableCell>
                                    <TableCell sx={getCellStyle(device.monitoring_status?.mikrotik_uptime, 'mikrotik_uptime')}>
                                        {device.monitoring_status?.mikrotik_uptime || 'Н/Д'}
                                    </TableCell>
                                    {/* --- КОНЕЦ НОВОГО --- */}
                                    <TableCell sx={getCellStyle(device.monitoring_status?.last_checked_at, 'last_checked_at')}>
                                        {getDisplayValue(device, 'last_checked_at')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default MonitoringPage;